import { env } from '../env'
import { getSelectedOrganizationId } from './organizationSelection'

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

// Only ApiError messages are user-facing (the server writes them in the UI
// locale); anything else — network failures, bugs — surfaces raw English like
// "Failed to fetch", so it gets the caller's localised fallback instead.
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

type Json = Record<string, unknown> | unknown[] | string | number | boolean | null

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: Json
  signal?: AbortSignal
  headers?: Record<string, string>
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers } = opts
  const url = path.startsWith('http') ? path : `${env.apiUrl}${path}`
  const isOrganizationMePath = path.startsWith('/organizations/me/')
  const organizationId = isOrganizationMePath ? getSelectedOrganizationId() : null
  // Client-side invariant, not a server error: org routes are only reachable
  // through RequireOrganization and org queries gate on the selected id, so
  // hitting this means a caller bypassed that flow. A plain Error (not
  // ApiError) is deliberate — errorMessage() shows the localized fallback.
  if (isOrganizationMePath && !organizationId)
    throw new Error('Organization context is not initialized')

  const requestHeaders = new Headers(headers)
  requestHeaders.set('Accept', 'application/json')
  if (body !== undefined) requestHeaders.set('Content-Type', 'application/json')
  if (organizationId) requestHeaders.set('X-Bocify-Organization-Id', organizationId)

  const init: RequestInit = {
    method,
    credentials: 'include',
    signal,
    headers: requestHeaders,
  }
  if (body !== undefined) init.body = JSON.stringify(body)

  const res = await fetch(url, init)

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const parsed: unknown = text ? safeJson(text) : null

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'message' in parsed && typeof (parsed as { message: unknown }).message === 'string'
        ? (parsed as { message: string }).message
        : null) ?? `${method} ${path} failed with ${res.status}`
    throw new ApiError(res.status, parsed, message)
  }

  return parsed as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: Json, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: Json, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: Json, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  // DELETE is contractually 204 No Content; callers should not rely on a body.
  delete: (path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<void> =>
    request<void>(path, { ...opts, method: 'DELETE' }),
}
